import { onBeforeUnmount, watch } from 'vue';
import { useEmbeddedModalPosition } from '@/composables/useEmbeddedModalPosition';
import getEmbeddedInModule from '@/helpers/getEmbeddedInModule';
import InternalModules from '@/models/InternalModules';

export const EMBEDDED_ENLARGED_IMAGE_CLASS = 'embedded-enlarged-image';

/**
 * Keeps the visualizer's full-size image inside the host's visible band when embedded in the FSC host.
 *
 * Why: the embedded visualizer's EnlargeableImage teleports its full-size view to `<body>` as
 * `position: fixed; top: 0; height: 100%`. Embedded, that window is the iframe, which the host sizes
 * to the whole document, so the image is centred on the full document instead of the screen: it
 * flashes up from the thumbnail, then settles far below where the user is looking. The teleport
 * target sits outside every component, so the band is published on `<html>`, where the rule in
 * global.scss reads it — the same band `PopoverRoot` uses for modals.
 */
export const useEmbeddedEnlargedImagePosition = (): void => {
  // Boundary: FSC casemanagement host only. SIU grows its iframe to the content height but never
  // sends a position sync, so the band would stay at the iframe's starting height and squeeze the
  // image; there, and standalone, the library's own full-window layout is kept.
  if (getEmbeddedInModule() !== InternalModules.ScreeningCenter) {
    return;
  }

  const { containerStyle } = useEmbeddedModalPosition();
  const root = document.documentElement;
  root.classList.add(EMBEDDED_ENLARGED_IMAGE_CLASS);

  watch(
    containerStyle,
    (style) => {
      for (const [name, value] of Object.entries(style)) {
        root.style.setProperty(name, value);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    root.classList.remove(EMBEDDED_ENLARGED_IMAGE_CLASS);
    for (const name of Object.keys(containerStyle.value)) {
      root.style.removeProperty(name);
    }
  });
};
